import React, { Suspense } from "react"
import { Avatar, Card, Divider, Skeleton } from "antd"
import { useTranslation } from "react-i18next"

import { CollectionNameEnum } from "@finway-group/shared/lib/models"
import { lazily } from "Shared/utils/react.utils"
import { Loading } from "Components/loading"
import { AuthzService } from "Shared/services"
import { NoSearchDataFound } from "Components/NoSearchDataFound"
import { generatePath, useHistory, useLocation, useParams } from "react-router-dom"
import { DELIVERY_NOTES_QUERY_PARAMS } from "Shared/services/localstorage.service"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { DeliveryNoteFormValues } from "Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { useDrawer } from "../purchaseOrders/hooks/useDrawer.hook"
import { DeliveryNoteDetailsHeader } from "./components/DeliveryNoteDetailsHeader"
import { DeliveryNotesDetailsSection } from "./components/DeliveryNotesDetailsSection"
import { LineItemCardReadOnly } from "./components/LineItemCardReadOnly"
import { LinkedPurchaseOrdersSection } from "./components/LinkedPurchaseOrdersSection"
import { DeliveryNoteDrawer } from "./components/DelieryNoteDrawer"
import { useMutateDeliveryNotes } from "./hooks/useDeliveryNotes.hook"
import { TruckIcon } from "./icons/TruckIcon"
import { useAdjacentDeliveryNotesQuery, useDeliveryNoteQuery } from "./queries/deliveryNote.queries"
import { useGetQueryParamsStorage } from "./hooks/usePaginationStorage.hook"
import { FileViewerDetails } from "../purchaseOrders/components/FileViewerDetails"
import { DeliveryNotesStatusTag } from "./components/DeliveryNotesStatusTag"
import { useDeliveryNoteFilter } from "./hooks/useDeliveryNoteFilter"

interface DnSwitchingLocationState {
    dnSwitchingSteps?: number
}

const { CommentContainer } = lazily(() => import("Components/comment/comment.container"))

export const DeliveryNoteDetailsPage = () => {
    const { id: deliveryNoteId } = useParams<{ id: string }>()
    const { t } = useTranslation()
    const queryParams = useGetQueryParamsStorage({ storageKey: DELIVERY_NOTES_QUERY_PARAMS })
    const { filterQuery } = useDeliveryNoteFilter()
    const { data: adjacentDeliveryNotes } = useAdjacentDeliveryNotesQuery({
        id: deliveryNoteId,
        enabled: Boolean(deliveryNoteId),
        queryParams: {
            ...queryParams,
            filter: filterQuery,
        },
    })
    const { data: dn, isLoading: isDeliveryNoteLoading, isError: isDeliveryNoteError } = useDeliveryNoteQuery(deliveryNoteId)

    const history = useHistory()
    const location = useLocation<DnSwitchingLocationState>()
    const { deleteDeliveryNote } = useMutateDeliveryNotes()

    const { isDrawerOpen, openDrawer, closeDrawer } = useDrawer<DeliveryNoteFormValues>()

    if (isDeliveryNoteLoading) {
        return <Loading />
    }

    // Early return for error state
    if (isDeliveryNoteError || !dn) {
        return (
            <div className="max-w-[1200px] mx-auto p-6">
                <NoSearchDataFound />
            </div>
        )
    }
    const canUserEdit = AuthzService.hasUserUpdateRightsForDeliveryNote(dn)

    return (
        <>
            <DeliveryNoteDetailsHeader
                title={
                    <div className="flex items-center space-x-4">
                        <h1 className="mr-6">{`${t("deliveryNote:details.title")} #${dn.deliveryNoteNumber}`}</h1>
                    </div>
                }
                editable={canUserEdit}
                deliveryNote={dn}
                onBack={() => {
                    if (location.state?.dnSwitchingSteps) {
                        history.go(0 - (location.state.dnSwitchingSteps + 1))
                    } else {
                        history.goBack()
                    }
                }}
                onEdit={
                    canUserEdit
                        ? () => {
                              openDrawer()
                          }
                        : undefined
                }
                onDelete={
                    canUserEdit
                        ? () => {
                              deleteDeliveryNote(dn._id)
                              history.push(RoutePathEnum.DELIVERY_NOTES)
                          }
                        : undefined
                }
                onNext={
                    adjacentDeliveryNotes?.nextId
                        ? () => {
                              history.push(generatePath(RoutePathEnum.DELIVERY_NOTE_DETAILS_PAGE, { id: adjacentDeliveryNotes.nextId! }), {
                                  dnSwitchingSteps: (location.state?.dnSwitchingSteps ?? 0) + 1,
                              })
                          }
                        : undefined
                }
                onPrev={
                    adjacentDeliveryNotes?.prevId
                        ? () => {
                              history.push(generatePath(RoutePathEnum.DELIVERY_NOTE_DETAILS_PAGE, { id: adjacentDeliveryNotes.prevId! }), {
                                  dnSwitchingSteps: (location.state?.dnSwitchingSteps ?? 0) + 1,
                              })
                          }
                        : undefined
                }
            />
            <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-9 gap-24 grid-rows-auto1fr">
                {/* Left column */}
                <div className="md:col-span-3 lg:col-span-6 md:row-span-2 md:order-first h-auto">
                    <Card>
                        <div className="flex items-center justify-between gap-16">
                            <div className="flex items-center gap-16">
                                <Avatar
                                    src={<TruckIcon />}
                                    size={48}
                                    className="text-indigo-600 font-bold text-2xl flex items-center justify-center border-none bg-blue-200 bg-opacity-10"
                                />
                                <div className="flex flex-col gap-2 flex-0">
                                    <div className="flex items-center gap-4">
                                        <span className="text-lg font-bold">{dn.vendor.name}</span>
                                    </div>

                                    <div className="truncate w-300" style={{ color: "#64748B" }}>
                                        {dn.description}
                                    </div>
                                </div>
                            </div>

                            <DeliveryNotesStatusTag deliveryNote={dn} />
                        </div>
                        <div
                            style={{
                                marginTop: 24,
                                border: "1px solid #F1F5F9",
                                borderRadius: 12,
                                padding: 12,
                                background: "#fff",
                            }}
                        >
                            {dn.lineItems.map((item, idx: number) => (
                                <div key={idx} className="mb-12 line-item-list">
                                    <LineItemCardReadOnly item={item} idx={idx} />

                                    {idx !== dn.lineItems.length - 1 && <Divider />}
                                </div>
                            ))}
                        </div>
                    </Card>
                    <LinkedPurchaseOrdersSection deliveryNoteId={deliveryNoteId} disabled={!canUserEdit} />
                </div>

                {/* Right column */}
                <div className="md:col-span-2 lg:col-span-3">
                    <FileViewerDetails
                        file={{
                            name: dn.deliveryNoteDocument?.name,
                            url: dn.deliveryNoteDocument?.url,
                        }}
                        isDisabled={!canUserEdit}
                        editRecord={() => openDrawer()}
                        title={t("deliveryNote:details.document")}
                        buttonText="action:add_delivery_note"
                    />

                    <Suspense fallback={<Skeleton active paragraph={{ rows: 2 }} />}>
                        <DeliveryNotesDetailsSection deliveryNote={dn} />
                    </Suspense>
                    <CommentContainer type={CollectionNameEnum.DELIVERY_NOTE} comments={dn.comments} object={{ id: dn._id }} />
                </div>
            </div>

            <DeliveryNoteDrawer open={isDrawerOpen} onClose={closeDrawer} editMode={Boolean(dn)} deliveryNote={dn} />
        </>
    )
}
